import { HistoryOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Card, Flex, Skeleton, Table, Tabs, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  fetchSessions,
  type ActiveSubscriberSession,
  type SubscriberSessionHistory,
} from '@/features/sessions/sessionsApi'
import { queryKeys } from '@/shared/api/queryKeys'
import {
  dash,
  formatBytes,
  formatDateTime,
  formatDuration,
  getDisplayTimeZone,
  zoneOffsetLabel,
} from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { LoadError } from '@/shared/ui/LoadError'

/**
 * Sessiyalar — backend IKKI alohida ro'yxat qaytaradi (`active`/`recent`,
 * `SessionsController`), shuning uchun UI ham ikkiga bo'lingan (bitta
 * jadval emas): "hozir ulangan" va "tarix" abonent uchun boshqa-boshqa
 * savolga javob beradi.
 */
export function SessionsPage() {
  const { t } = useTranslation()
  /*
    ⚠️ Faol sessiya — sahifadagi eng tez o'zgaradigan narsa: qurilma
    ulanadi, uziladi, trafik o'sadi. Yangilanishsiz abonent "hozir
    ulanganmanmi?" degan savolga ESKI javob olardi, ya'ni sahifa aynan
    o'zi javob berishi kerak bo'lgan savolda foydasiz edi.
  */
  const sessionsQuery = useQuery({
    queryKey: queryKeys.sessions,
    queryFn: fetchSessions,
    refetchInterval: 20_000,
  })

  return (
    <Flex vertical gap={16}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('sessions.title')}
        </Typography.Title>
        <Typography.Text type="secondary">{t('sessions.subtitle')}</Typography.Text>
        {/* Sessiya vaqtlari operator zonasida — qurilma zonasi boshqa bo'lsa farq ko'rinadi */}
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
          {t('timezone.shownIn', { zone: getDisplayTimeZone(), offset: zoneOffsetLabel() })}
        </Typography.Text>
      </div>

      {sessionsQuery.isPending ? (
        <Card><Skeleton active paragraph={{ rows: 6 }} /></Card>
      ) : sessionsQuery.isError ? (
        <LoadError
          titleKey="sessions.loadFailed"
          error={sessionsQuery.error}
          onRetry={() => void sessionsQuery.refetch()}
        />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Tabs
            style={{ padding: '0 16px' }}
            items={[
              {
                key: 'active',
                label: `${t('sessions.tabActive')} (${sessionsQuery.data.active.length})`,
                children:
                  sessionsQuery.data.active.length === 0 ? (
                    <div style={{ padding: 24 }}>
                      <EmptyState icon={<HistoryOutlined />} title={t('sessions.emptyActive')} />
                    </div>
                  ) : (
                    <Table<ActiveSubscriberSession>
                      rowKey="radacctId"
                      dataSource={sessionsQuery.data.active}
                      pagination={false}
                      scroll={{ x: true }}
                      columns={[
                        {
                          title: t('sessions.columns.startedAt'),
                          dataIndex: 'startedAt',
                          render: (v: string) => formatDateTime(v),
                        },
                        {
                          title: t('sessions.columns.duration'),
                          dataIndex: 'durationSeconds',
                          render: (v: number) => formatDuration(v),
                        },
                        {
                          title: t('sessions.columns.ip'),
                          dataIndex: 'ipAddress',
                          render: (v: string | null) => dash(v),
                        },
                        {
                          title: t('sessions.columns.device'),
                          dataIndex: 'device',
                          render: (v: string | null) => dash(v),
                        },
                        {
                          title: t('sessions.columns.traffic'),
                          key: 'traffic',
                          render: (_: unknown, row: ActiveSubscriberSession) =>
                            formatBytes(row.uploadBytes + row.downloadBytes),
                        },
                      ]}
                    />
                  ),
              },
              {
                key: 'history',
                label: t('sessions.tabHistory'),
                children:
                  sessionsQuery.data.recent.length === 0 ? (
                    <div style={{ padding: 24 }}>
                      <EmptyState icon={<HistoryOutlined />} title={t('sessions.empty')} />
                    </div>
                  ) : (
                    <>
                      <Table<SubscriberSessionHistory>
                        rowKey="radacctId"
                        dataSource={sessionsQuery.data.recent}
                        pagination={{ pageSize: 10, hideOnSinglePage: true }}
                        scroll={{ x: true }}
                        columns={[
                          {
                            title: t('sessions.columns.startedAt'),
                            dataIndex: 'startedAt',
                            render: (v: string) => formatDateTime(v),
                          },
                          {
                            title: t('sessions.columns.endedAt'),
                            dataIndex: 'endedAt',
                            render: (v: string | null) =>
                              v ? formatDateTime(v) : <Tag color="success">{t('sessions.ongoing')}</Tag>,
                          },
                          {
                            title: t('sessions.columns.duration'),
                            dataIndex: 'durationSeconds',
                            render: (v: number) => formatDuration(v),
                          },
                          {
                            title: t('sessions.columns.ip'),
                            dataIndex: 'ipAddress',
                            render: (v: string | null) => dash(v),
                          },
                          {
                            title: t('sessions.columns.device'),
                            dataIndex: 'device',
                            render: (v: string | null) => dash(v),
                          },
                          {
                            title: t('sessions.columns.traffic'),
                            key: 'traffic',
                            render: (_: unknown, row: SubscriberSessionHistory) =>
                              formatBytes(row.uploadBytes + row.downloadBytes),
                          },
                        ]}
                      />
                      {sessionsQuery.data.hasMore ? (
                        <Typography.Text
                          type="secondary"
                          style={{ display: 'block', padding: '8px 8px 16px', fontSize: 12 }}
                        >
                          {t('sessions.hasMoreHint')}
                        </Typography.Text>
                      ) : null}
                    </>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </Flex>
  )
}
